import { useEffect, useRef, useState } from "react";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import CircleGeom from "ol/geom/Circle";
import { fromLonLat } from "ol/proj";
import { Style, Icon, Stroke, Fill } from "ol/style";
import { Button, Typography } from "antd";
import { AimOutlined } from "@ant-design/icons";
import { XYZ } from "ol/source";
import { boundingExtent } from "ol/extent";
import { BASE, distanceKm } from "../utils/location";

const { Text } = Typography;

export default function TakeawayTrackLocation() {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const userFeatureRef = useRef(null);
  const routeFeatureRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastRouteFetch = useRef(0);
  const hasFitInitially = useRef(false);

  const [distance, setDistance] = useState(0);
  const [eta, setEta] = useState(0);
  const [mode, setMode] = useState("driving");

  /* ---------------- INIT MAP ---------------- */
  useEffect(() => {
    const userSource = new VectorSource();
    const routeSource = new VectorSource();

    const map = new Map({
      target: mapDivRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
          }),
        }),
        new VectorLayer({ source: routeSource }),
        new VectorLayer({ source: userSource }),
      ],
      view: new View({
        center: fromLonLat([BASE.lng, BASE.lat]),
        zoom: 15,
      }),
    });

    mapRef.current = map;

    /* ---------- OUTLET ---------- */
    const outlet = new Feature(
      new Point(fromLonLat([BASE.lng, BASE.lat]))
    );
    outlet.setStyle(
      new Style({
        image: new Icon({
          src: "/image/logo_pin.png",
          scale: 0.5,
        }),
      })
    );
    userSource.addFeature(outlet);
 
    /* ---------- USER ICON ---------- */
    const userFeature = new Feature(
      new Point(fromLonLat([BASE.lng, BASE.lat]))
    );
    userFeature.setStyle(
      new Style({
        image: new Icon({
          src: "/image/bike-delivery.png",
          scale: 0.25,
          rotateWithView: true,
        }),
      })
    );
    userSource.addFeature(userFeature);
    userFeatureRef.current = userFeature;

    /* ---------- ROUTE ---------- */
    const routeFeature = new Feature(new LineString([]));
    routeFeature.setStyle(
      new Style({
        stroke: new Stroke({ color: "#1677ff", width: 5 }),
      })
    );
    routeSource.addFeature(routeFeature);
    routeFeatureRef.current = routeFeature;

    return () => map.setTarget(null);
  }, []);

  /* ---------------- FIT USER + OUTLET (ONCE) ---------------- */
  const fitUserAndOutlet = (lat, lng) => {
    const view = mapRef.current.getView();

    const userCoord = fromLonLat([lng, lat]);
    const outletCoord = fromLonLat([BASE.lng, BASE.lat]);

    const extent = boundingExtent([userCoord, outletCoord]);

    view.fit(extent, {
      padding: [80, 80, 80, 80],
      maxZoom: 16,
      duration: 600,
    });
  };

  /* ---------------- FOLLOW USER ---------------- */
  const centerToUser = (lat, lng) => {
    if (!hasFitInitially.current) {
      fitUserAndOutlet(lat, lng);
      hasFitInitially.current = true;
      return;
    }

    mapRef.current.getView().animate({
      center: fromLonLat([lng, lat]),
      duration: 300,
    });
  };

  /* ---------------- FETCH ROUTE ---------------- */
  const fetchRoute = async (lat, lng, travelMode) => {
    const profile = travelMode === "walking" ? "foot" : "driving";
    const url = `https://router.project-osrm.org/route/v1/${profile}/${lng},${lat};${BASE.lng},${BASE.lat}?overview=full&geometries=geojson`;

    const res = await fetch(url);
    const data = await res.json();
    if (!data.routes?.length) return;

    const coords = data.routes[0].geometry.coordinates.map((c) =>
      fromLonLat(c)
    );

    routeFeatureRef.current.setGeometry(new LineString(coords));
    setDistance((data.routes[0].distance / 1000).toFixed(2));
  };

  /* ---------------- LIVE GPS ---------------- */
  useEffect(() => {
    if (!navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const { latitude, longitude, speed, heading } = p.coords;

        const pos = fromLonLat([longitude, latitude]);
        userFeatureRef.current.getGeometry().setCoordinates(pos);

        userFeatureRef.current
          .getStyle()
          .getImage()
          .setRotation(((heading || 0) * Math.PI) / 180);

        centerToUser(latitude, longitude);

        const travelMode = speed < 2 ? "walking" : "driving";
        setMode(travelMode);

        const d = distanceKm(BASE.lat, BASE.lng, latitude, longitude);
        const kmh = speed ? speed * 3.6 : travelMode === "walking" ? 4 : 30;
        setEta(Math.ceil((d / kmh) * 60));

        const now = Date.now();
        if (now - lastRouteFetch.current > 5000) {
          lastRouteFetch.current = now;
          fetchRoute(latitude, longitude, travelMode);
        }
      },
      (e) => console.error("GPS error", e),
      { enableHighAccuracy: true, maximumAge: 1000 }
    );

    return () => navigator.geolocation.clearWatch(watchIdRef.current);
  }, []);

  /* ---------------- CENTER BUTTON ---------------- */
  const centerMap = () => {
    const geom = userFeatureRef.current?.getGeometry();
    if (!geom) return;

    mapRef.current.getView().animate({
      center: geom.getCoordinates(),
      zoom: mapRef.current.getView().getZoom(),
      duration: 500,
    }); 
  };

  return (
    <>
      <div style={{ position: "relative" }}>
        <div ref={mapDivRef} style={{ height: 400, width: "100%" }} />

        <Button
          type="primary"
          shape="circle"
          icon={<AimOutlined />}
          onClick={centerMap}
          style={{
            position: "absolute",
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        />
      </div>

      {/* <br /> */}

      {/* <Text>Mode: {mode}</Text><br />
      <Text>Distance: {distance} km</Text><br />
      <Text>Estimated Time: {formatETA(eta)}</Text> */}
    </>
  );
}
